import { Fragment, h } from 'preact';
import PropTypes from 'prop-types';
import { useEffect } from 'preact/hooks';

import { authStore } from '../../../global-store/subjects/auth';
import { useSubscribe } from '../../hooks/use-subscribe';
import Loader from '../../components/2-atoms/loader/loader';
import SkyItConfirm from '../../components/3-molecules/sky-it-confirm/sky-it-confirm';
import OverlayWrapper from '../../components/3-molecules/overlay-wrapper/overlay-wrapper';
import StatusNotification from '../../components/2-atoms/status-notification/status-notification';
import { getInitialState } from '../../../global-store/subjects/user-preferences';
import { initDatalayerElement } from '../../../components/tracking/datalayer';

import { getHandlers } from './getters/get-handlers';
import { renderHeroAndFeatureList, renderTabsAndContent } from './renderers';
import { getDataFromBlock } from './getters/get-data-from-block';

const UserPreferences = ({ htmlProps, widget, state, actions, WPNCtx, newslettersCtx, parselyCtx, $root }) => {
  const authState = useSubscribe(authStore);
  const { isUserLogged, isLoginLoading } = authState;

  const handlers = getHandlers({
    htmlProps,
    widget,
    state,
    actions,
    WPNCtx,
    parselyCtx,
    newslettersCtx,
  });
  const pageIsLoading =
    isLoginLoading ||
    (WPNCtx.htmlProps && WPNCtx.state.isLoading) ||
    (parselyCtx.myTeam.htmlProps && isUserLogged && parselyCtx.myTeam.state.isLoading) ||
    (parselyCtx.mySport.htmlProps && isUserLogged && parselyCtx.mySport.state.isLoading) ||
    (parselyCtx.myLocalNews.htmlProps && isUserLogged && parselyCtx.myLocalNews.state.isLoading) ||
    (newslettersCtx.htmlProps && isUserLogged && newslettersCtx.state.isWidgetLoading) ||
    (isUserLogged && state.premiumStatus.isLoading);

  const heroBlock = htmlProps.blocks.find((element) => element.type === widget.blockTypes.hero);

  useEffect(() => {
    if (
      parselyCtx.myTeam.state.contentsByUserPreferences.length ||
      parselyCtx.mySport.state.contentsByUserPreferences.length ||
      parselyCtx.myLocalNews.state.contentsByUserPreferences.length ||
      state.selectedTab !== getInitialState().selectedTab
    ) {
      initDatalayerElement($root);
    }
  }, [
    parselyCtx.myTeam.state.contentsByUserPreferences,
    parselyCtx.mySport.state.contentsByUserPreferences,
    parselyCtx.myLocalNews.state.contentsByUserPreferences,
    state.selectedTab,
  ]);

  if (isLoginLoading) {
    return <Loader modifiers={['medium']} wrapperModifiers={['fixed']} withWrapper />;
  }

  return (
    <Fragment>
      {renderHeroAndFeatureList({
        data: getDataFromBlock({ block: heroBlock, widget, isUserLogged }),
        handlers,
        isUserLogged,
      })}
      {renderTabsAndContent({
        blocks: htmlProps.blocks,
        selectedTab: state.selectedTab,
        widget,
        pageState: state,
        pageActions: actions,
        WPNCtx,
        newslettersCtx,
        parselyCtx,
        pageHandlers: handlers,
        isUserLogged,
        $root,
      })}
      {pageIsLoading && <Loader modifiers={['medium']} wrapperModifiers={['fixed']} withWrapper />}
      {state.confirmBottomDialog.isShown && (
        <Fragment>
          <OverlayWrapper showOverlay={state.confirmBottomDialog.isShown} />
          <SkyItConfirm
            onConfirm={handlers.handleConfirmAccept}
            onCancel={handlers.handleConfirmCancel}
            ctaPrimaryLabel={state.confirmBottomDialog.ctaPrimaryLabel}
            message={state.confirmBottomDialog.message}
          />
        </Fragment>
      )}
      {state.notificationStatus.isShown && (
        <StatusNotification message={state.notificationStatus.message} modifiers={['success']} hasCheckIcon />
      )}
    </Fragment>
  );
};

UserPreferences.propTypes = {
  htmlProps: PropTypes.shape({
    blocks: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  widget: PropTypes.shape({
    name: PropTypes.string.isRequired,
    htmlId: PropTypes.string.isRequired,
    elementIds: PropTypes.object.isRequired,
    loggedKey: PropTypes.string.isRequired,
    notLoggedKey: PropTypes.string.isRequired,
    blockTypes: PropTypes.object.isRequired,
  }),
  state: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  parselyCtx: PropTypes.object.isRequired,
  newslettersCtx: PropTypes.object.isRequired,
  WPNCtx: PropTypes.object.isRequired,
  $root: PropTypes.object,
};

export default UserPreferences;
